/**
 * Canonical expiry-date display format for anything that is physically printed
 * on a dispensed product (vial label, bag label, receipt).
 *
 * Two representations of the same expiry exist in the system:
 *  - what the technician TYPES and what every renderer prints — `MM/YYYY`
 *    (or `DD/MM/YYYY` when the pack carries a day), and
 *  - what is PERSISTED to `metadata.packagingData[].expiryDate` — ISO
 *    (`YYYY-MM-DD`), written by PackagingVerification's `convertMMYYYYToISO`.
 *
 * `convertMMYYYYToISO` stores day `01` as the "no day was given" sentinel for a
 * typed `MM/YYYY`, so ISO → display maps `YYYY-MM-01` back to `MM/YYYY` — a
 * reprint that reads the persisted value therefore renders the SAME string the
 * original label carried, instead of `01/MM/YYYY` or a raw ISO date (GP-1322).
 *
 * Idempotent: already-display-formatted values are returned unchanged, so it is
 * safe to apply at the source AND in a renderer.
 */
export const formatExpiryForDisplay = (expiry: string): string => {
  if (!expiry) return '';
  // Already display format (MM/YYYY or DD/MM/YYYY) — leave it alone.
  if (/^\d{2}\/\d{4}$/.test(expiry)) return expiry;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(expiry)) return expiry;
  // YYYY-MM-DD → DD/MM/YYYY, except the day-01 sentinel which means "no day".
  if (/^\d{4}-\d{2}-\d{2}$/.test(expiry)) {
    const [year, month, day] = expiry.split('-');
    return day === '01' ? `${month}/${year}` : `${day}/${month}/${year}`;
  }
  // YYYY-MM → MM/YYYY
  if (/^\d{4}-\d{2}$/.test(expiry)) {
    const [year, month] = expiry.split('-');
    return `${month}/${year}`;
  }
  return expiry;
};
